import React, { useCallback, useId, useMemo, useState } from 'react';

import type { UUID } from '@aline/common';
import { ButtonBase, Collapse, IconButton } from '@mui/material';
import copy from 'copy-to-clipboard';
import { Copy } from 'lucide-react';
import { useAssistantMessageHandlers } from 'src/components/ai/chat/chat-hooks/useAssistantMessageHandlers';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { SegmentedControl } from 'src/components/generic/segmented-control';
import type { SegmentedControlOption } from 'src/components/generic/segmented-control';
import { Spacer } from 'src/components/generic/Spacer';
import { ChatChevronIcon } from 'src/components/icons/ChatChevronIcon';
import { pushNotification } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import {
  buildAnalysisTableMarkdown,
  formatRowCount,
  getAnalysisSourceRowCount,
  getInlineAnalysisColumns,
  getRowCountLabel,
} from './analysis-result-rows';
import type {
  AnalysisColumn,
  AnalysisColumnLabels,
  AnalysisPanelSource,
  AnalysisRow,
} from './analysis-result-rows';
import { AnalysisResultTable } from './analysis-result-table';
import { formatAnalysisSql, getSqlText } from './format-analysis-sql';
import type { SqlTokenKind } from './format-analysis-sql';
import { useAnalysisQueryRows } from './use-analysis-query-rows';

const ANALYSIS_PANEL_TABS = {
  preview: 'preview',
  sql: 'sql',
} as const;

type AnalysisPanelTab = (typeof ANALYSIS_PANEL_TABS)[keyof typeof ANALYSIS_PANEL_TABS];

const TAB_OPTIONS: readonly SegmentedControlOption[] = [
  { label: 'Preview', value: ANALYSIS_PANEL_TABS.preview },
  { label: 'SQL', value: ANALYSIS_PANEL_TABS.sql },
];

/**
 * Colors for the SQL view. The theme has no code palette, and these are picked
 * to stay readable on the white code background.
 */
const SQL_TOKEN_COLORS: Record<SqlTokenKind, string | undefined> = {
  function: '#8250DF',
  identifier: undefined,
  keyword: '#CF222E',
  number: '#0550AE',
  param: '#0550AE',
  plain: undefined,
  string: '#116329',
};

/** Characters a wrapped line hangs past the start of the line it continues. */
const HANGING_INDENT_CH = 2;

const useStyles = makeStyles()(theme => ({
  chevron: {
    transition: 'transform 120ms ease',
  },
  chevronOpen: {
    transform: 'rotate(90deg)',
  },
  code: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 6,
    minWidth: 0,
    overflow: 'hidden',
  },
  codeFooter: {
    borderTop: `1px solid ${theme.appColors.rule}`,
    color: theme.palette.text.secondary,
    fontSize: 12,
    padding: theme.spacing(0.5, 1),
  },
  codeLine: {
    overflowWrap: 'anywhere',
    textIndent: `-${HANGING_INDENT_CH}ch`,
    whiteSpace: 'pre-wrap',
  },
  codeLines: {
    fontFamily: 'monospace',
    fontSize: 12,
    lineHeight: 1.6,
    maxHeight: 320,
    overflowY: 'auto',
    padding: theme.spacing(1),
  },
  /**
   * A line wraps between tokens and never inside one. Left to itself the
   * browser breaks after a hyphen, which splits `->>` across two lines.
   */
  codeToken: {
    whiteSpace: 'pre',
  },
  copyButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.common.black,
    },
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.5),
  },
  notice: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  panel: {
    backgroundColor: theme.appColors.background,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    minWidth: 0,
    padding: theme.spacing(1),
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  status: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    padding: theme.spacing(1.5, 1),
  },
  toolbar: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  trigger: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    borderRadius: 4,
    display: 'flex',
    fontFamily: 'inherit',
    fontSize: 12,
    gap: theme.spacing(0.5),
    userSelect: 'none',
  },
  triggerCount: {
    color: theme.palette.text.secondary,
  },
}));

interface AnalysisQueryPanelProps {
  columnLabels: AnalysisColumnLabels;
  source: AnalysisPanelSource;
  sql: string;
}

/**
 * The query an analysis tool call ran and the rows it returned, in one section
 * that starts closed. The answer above the card is what most readers want, and
 * the query is there for the ones who want to check it.
 *
 * Nothing under the section is mounted until it opens, so a thread with many
 * of these cards neither renders a table nor fetches rows for each.
 */
export const AnalysisQueryPanel = React.memo(function AnalysisQueryPanel({
  columnLabels,
  source,
  sql,
}: AnalysisQueryPanelProps): JSX.Element {
  const panelId = useId();
  const { classes, cx } = useStyles();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<AnalysisPanelTab>(ANALYSIS_PANEL_TABS.preview);

  const handleToggle = useCallback(() => setOpen(previous => !previous), []);
  const handleTabChange = useCallback((value: string) => {
    if (getIsAnalysisPanelTab(value)) setActiveTab(value);
  }, []);

  return (
    <div className={classes.root}>
      <ButtonBase
        aria-controls={open ? panelId : undefined}
        aria-expanded={open}
        className={classes.trigger}
        onClick={handleToggle}
      >
        <span>Search results</span>
        <span className={classes.triggerCount}>
          {`· ${getRowCountLabel(getAnalysisSourceRowCount(source))}`}
        </span>
        <ChatChevronIcon
          className={cx(classes.chevron, open && classes.chevronOpen)}
          height={14}
          width={14}
        />
      </ButtonBase>
      <Collapse unmountOnExit in={open}>
        <div className={classes.panel} id={panelId}>
          <AnalysisPanelBody
            activeTab={activeTab}
            columnLabels={columnLabels}
            source={source}
            sql={sql}
            onTabChange={handleTabChange}
          />
        </div>
      </Collapse>
    </div>
  );
});

interface AnalysisPanelBodyProps {
  activeTab: AnalysisPanelTab;
  columnLabels: AnalysisColumnLabels;
  onTabChange: (value: string) => void;
  source: AnalysisPanelSource;
  sql: string;
}

function AnalysisPanelBody({
  columnLabels,
  source,
  ...props
}: AnalysisPanelBodyProps): JSX.Element {
  switch (source.kind) {
    case 'inline':
      return <InlineAnalysisPanelBody {...props} columnLabels={columnLabels} rows={source.rows} />;
    case 'paged':
      return (
        <PagedAnalysisPanelBody
          {...props}
          messageId={source.messageId}
          toolCallId={source.toolCallId}
        />
      );
  }
}

function InlineAnalysisPanelBody({
  columnLabels,
  rows,
  ...props
}: Omit<AnalysisPanelBodyProps, 'source'> & { rows: AnalysisRow[] }): JSX.Element {
  const columns = useMemo(() => getInlineAnalysisColumns(rows, columnLabels), [columnLabels, rows]);

  return (
    <AnalysisPanelContent
      {...props}
      columns={columns}
      footer={getRowCountLabel(rows.length)}
      rowCount={rows.length}
      rows={rows}
    />
  );
}

/**
 * Rows read from the recorded query, a page at a time as the reader scrolls.
 * They are the rows as they are now, so the card says so when they no longer
 * match what the answer was written from.
 */
function PagedAnalysisPanelBody({
  messageId,
  toolCallId,
  ...props
}: Omit<AnalysisPanelBodyProps, 'columnLabels' | 'source'> & {
  messageId: UUID;
  toolCallId: string;
}): JSX.Element {
  const {
    answeredTotalCount,
    changedSinceAnswer,
    columns,
    error,
    hasMore,
    loadMore,
    loadMoreFailed,
    loading,
    loadingMore,
    rows,
    totalCount,
  } = useAnalysisQueryRows({ messageId, toolCallId });
  const handleEndReached = useCallback(() => {
    if (hasMore) void loadMore();
  }, [hasMore, loadMore]);

  return (
    <AnalysisPanelContent
      {...props}
      columns={columns}
      footer={getPagedFooter({ loadMoreFailed, loadedCount: rows.length, loadingMore, totalCount })}
      notice={changedSinceAnswer ? getChangedNotice({ answeredTotalCount, totalCount }) : null}
      rowCount={totalCount}
      rows={rows}
      tableStatus={getPagedTableStatus({ hasError: Boolean(error), loading })}
      onEndReached={handleEndReached}
    />
  );
}

interface AnalysisPanelContentProps {
  activeTab: AnalysisPanelTab;
  columns: AnalysisColumn[];
  footer: string;
  onTabChange: (value: string) => void;
  /** How many rows the query returns, which can be more than are loaded. */
  rowCount: number;
  rows: AnalysisRow[];
  sql: string;
  notice?: string | null;
  onEndReached?: () => void;
  /** Shown on the preview tab in place of the table, while there is none to show. */
  tableStatus?: string | null;
}

function AnalysisPanelContent({
  activeTab,
  columns,
  notice,
  onTabChange,
  rows,
  sql,
  ...viewProps
}: AnalysisPanelContentProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <>
      <div className={classes.toolbar}>
        <SegmentedControl
          ariaLabel="Query result view"
          options={TAB_OPTIONS}
          value={activeTab}
          onChange={onTabChange}
        />
        <Spacer />
        <AnalysisCopyButton activeTab={activeTab} columns={columns} rows={rows} sql={sql} />
      </div>
      {Boolean(notice) && <div className={classes.notice}>{notice}</div>}
      <AnalysisPanelView
        activeTab={activeTab}
        columns={columns}
        rows={rows}
        sql={sql}
        {...viewProps}
      />
    </>
  );
}

function AnalysisPanelView({
  activeTab,
  columns,
  footer,
  onEndReached,
  rowCount,
  rows,
  sql,
  tableStatus,
}: Omit<AnalysisPanelContentProps, 'notice' | 'onTabChange'>): JSX.Element {
  const { classes } = useStyles();
  if (activeTab === ANALYSIS_PANEL_TABS.sql)
    return <AnalysisSqlView rowCount={rowCount} sql={sql} />;
  if (tableStatus) return <div className={classes.status}>{tableStatus}</div>;
  return (
    <AnalysisResultTable
      columns={columns}
      footer={footer}
      rows={rows}
      onEndReached={onEndReached}
    />
  );
}

function AnalysisSqlView({ rowCount, sql }: { rowCount: number; sql: string }): JSX.Element {
  const { classes } = useStyles();
  const lines = useMemo(() => formatAnalysisSql(sql, { collapseParams: true }), [sql]);

  return (
    <div className={classes.code}>
      <div aria-label="SQL query" className={classes.codeLines} role="group">
        {lines.map((line, lineIndex) => (
          <div
            key={lineIndex}
            className={classes.codeLine}
            style={{ paddingLeft: `${line.indent * 2 + HANGING_INDENT_CH}ch` }}
          >
            {line.tokens.map((token, tokenIndex) => (
              <span
                key={tokenIndex}
                className={token.text.trim() ? classes.codeToken : undefined}
                style={{ color: SQL_TOKEN_COLORS[token.kind] }}
              >
                {token.text}
              </span>
            ))}
          </div>
        ))}
      </div>
      <div className={classes.codeFooter}>{getReturnedLabel(rowCount)}</div>
    </div>
  );
}

/**
 * Copies what the open tab shows. The table goes out as markdown so it pastes
 * as a table, with the rows loaded so far. The SQL goes out as plain text with
 * every placeholder written out, since the shortened list on screen is not a
 * query anyone could run.
 */
function AnalysisCopyButton({
  activeTab,
  columns,
  rows,
  sql,
}: {
  activeTab: AnalysisPanelTab;
  columns: AnalysisColumn[];
  rows: AnalysisRow[];
  sql: string;
}): JSX.Element {
  const { classes } = useStyles();
  const { copyMarkdownPromiseToClipboard } = useAssistantMessageHandlers();
  const isSqlTab = activeTab === ANALYSIS_PANEL_TABS.sql;
  const label = isSqlTab ? 'Copy SQL' : 'Copy table';

  const handleCopy = () => {
    if (isSqlTab) {
      copySql(sql);
      return;
    }
    const markdown = buildAnalysisTableMarkdown({ columns, rows });
    /**
     * The markdown is ready now, but the promise variant is the one that starts
     * the clipboard write inside the click. `copyMarkdownToClipboard` converts
     * to HTML first, and a browser refuses a write that arrives after the click
     * has gone stale. The clipboard then keeps what it held, which is the SQL
     * when that was copied a moment earlier.
     */
    copyMarkdownPromiseToClipboard(Promise.resolve(markdown), 'Table copied to clipboard.');
  };

  return (
    <AlineTooltip arrow placement="top" title={label}>
      {/* A disabled button fires no events, so the tooltip listens on the span. */}
      <span>
        <IconButton
          aria-label={label}
          className={classes.copyButton}
          disabled={!isSqlTab && rows.length === 0}
          size="small"
          onClick={handleCopy}
        >
          <Copy size={14} />
        </IconButton>
      </span>
    </AlineTooltip>
  );
}

function copySql(sql: string): void {
  if (!copy(getSqlText(formatAnalysisSql(sql)))) return;
  pushNotification({ key: uuid4(), message: 'SQL copied to clipboard.', variant: 'success' });
}

function getIsAnalysisPanelTab(value: string): value is AnalysisPanelTab {
  return value === ANALYSIS_PANEL_TABS.preview || value === ANALYSIS_PANEL_TABS.sql;
}

function getReturnedLabel(rowCount: number): string {
  if (rowCount === 0) return 'No rows returned';
  return `Total ${getRowCountLabel(rowCount)} returned`;
}

function getPagedTableStatus({
  hasError,
  loading,
}: {
  hasError: boolean;
  loading: boolean;
}): string | null {
  if (hasError) return 'The rows could not be loaded.';
  if (loading) return 'Loading rows…';
  return null;
}

function getPagedFooter({
  loadMoreFailed,
  loadedCount,
  loadingMore,
  totalCount,
}: {
  loadedCount: number;
  loadingMore: boolean;
  loadMoreFailed: boolean;
  totalCount: number;
}): string {
  const shown = `Showing ${loadedCount.toLocaleString('en-US')} of ${formatRowCount(totalCount)}`;
  if (loadMoreFailed) return `${shown} · More rows could not be loaded`;
  if (loadingMore) return `${shown} · Loading more…`;
  return shown;
}

function getChangedNotice({
  answeredTotalCount,
  totalCount,
}: {
  answeredTotalCount: number;
  totalCount: number;
}): string {
  const changed =
    'The documents changed after this answer was written, so these rows may not match it.';
  if (answeredTotalCount === totalCount) return changed;
  return `${changed} The query returned ${formatRowCount(answeredTotalCount)} then and returns ${formatRowCount(totalCount)} now.`;
}
